/**
 * PavementFormulaService - Servizio centralizzato per le formule specifiche di Excel
 *
 * Questo servizio contiene tutte le formule di calcolo specifiche che sono state
 * reverse-engineered dai fogli Excel, distinte dalle formule AASHTO standard.
 *
 * Scopo: Evitare hardcoding e centralizzare la logica di business Excel-specifica
 */

export class PavementFormulaService {
    /**
     * Calcola il Modulo Resiliente (Mr) in psi usando la formula specifica di Excel.
     *
     * Formula Excel scoperta: Mr (psi) = IF(CBR≤5, CBR×10×145, 17.621×CBR^0.64×145)
     * Questa formula è diversa da quella generica AASHTO (Mr = 1500 × CBR)
     *
     * Source: Design_LCR!O52 = IF(G41<=5,(G41*10),IF(G41>5,17.621*(G41^0.64)))*145
     *
     * @param cbr Il valore CBR del sottofondo
     * @returns Il valore di Mr in psi secondo la logica Excel
     */
    public static calculateSubgradeModulusExcel(cbr: number): number {
        if (cbr <= 0) {
            throw new Error('CBR deve essere maggiore di 0');
        }

        let mrBase: number;

        if (cbr <= 5) {
            // Formula Excel per CBR ≤ 5: CBR × 10
            mrBase = cbr * 10;
        } else {
            // Formula Excel per CBR > 5: 17.621 × CBR^0.64
            mrBase = 17.621 * Math.pow(cbr, 0.64);
        }

        // Moltiplicazione finale per conversione unità (145 = fattore psi)
        const mrPsi = mrBase * 145;

        return mrPsi;
    }

    /**
     * Calcola il Modulo Resiliente (Mr) in kPa usando la formula specifica di Excel.
     *
     * Formula Excel: Mr (kPa) = IF(CBR≤5, CBR×10×1000, 17.621×CBR^0.64×1000)
     *
     * Source: Design_LCR!G42 = IF(G41<=5,(G41*10)*1000,IF(G41>5,17.621*(G41^0.64)*1000))
     *
     * @param cbr Il valore CBR del sottofondo
     * @returns Il valore di Mr in kPa secondo la logica Excel
     */
    public static calculateSubgradeModulusExcelKPa(cbr: number): number {
        if (cbr <= 0) {
            throw new Error('CBR deve essere maggiore di 0');
        }

        let mrBase: number;

        if (cbr <= 5) {
            mrBase = cbr * 10;
        } else {
            mrBase = 17.621 * Math.pow(cbr, 0.64);
        }

        // Moltiplicazione finale per kPa (1000 = fattore kPa)
        const mrKPa = mrBase * 1000;

        return mrKPa;
    }

    /**
     * Calcola il Traffico di Progetto (ESAL) secondo la logica corretta di Excel.
     *
     * CORREZIONE CRITICA: Il fattore 44.37x era un artefatto del confronto
     * tra traffico calcolato (16.6M) e traffico definito dall'utente (9.4M).
     *
     * La logica corretta di Excel:
     * 1. L'utente inserisce un valore ESAL personalizzato (9,395,483.519)
     * 2. Questo valore viene usato DIRETTAMENTE senza amplificazione
     * 3. La differenza stabilizzato/non stabilizzato deriva dai moltiplicatori
     *    LCR applicati al Structural Number, non al traffico
     *
     * Riferimento: analisi_traffico.md - "Il fattore 44.37x non esiste"
     *
     * @param nominalESAL Il traffico ESAL inserito dall'utente
     * @returns Il traffico di progetto usato direttamente (senza amplificazione)
     */
    public static calculateProjectESALExcel(nominalESAL: number): number {
        if (nominalESAL <= 0) {
            throw new Error('ESAL nominale deve essere maggiore di 0');
        }

        // ✅ CORREZIONE: Uso diretto del valore utente senza amplificazione
        // Secondo analisi_traffico.md, il traffico utente va usato come specificato
        const projectESAL = nominalESAL;

        return projectESAL;
    }

    /**
     * Calcola il Structural Number stabilizzato applicando i moltiplicatori LCR.
     *
     * Questa è la VERA fonte della differenza tra scenari stabilizzati e non stabilizzati:
     * - SNunstabilised ≈ 6.78
     * - SNstabilised ≈ 9.96 (tramite moltiplicatori C8=1.588, C9=1.679)
     *
     * La formula AASHTO esponenziale amplifica questa differenza di SN in
     * enormi differenze di capacità di traffico.
     *
     * Riferimento: analisi_traffico.md - "L'intero, massiccio aumento di capacità
     * è dovuto al fatto che il calcolo di SNstabilised parte da presupposti molto
     * più favorevoli (grazie ai moltiplicatori C8 e C9)"
     *
     * @param baseSN Structural Number base non stabilizzato
     * @param baseLCR Moltiplicatore LCR per base (C8 ≈ 1.588)
     * @param subbaseLCR Moltiplicatore LCR per subbase (C9 ≈ 1.679)
     * @returns Structural Number stabilizzato con moltiplicatori applicati
     */
    public static calculateStabilisedSN(baseSN: number, baseLCR: number, subbaseLCR: number): number {
        if (baseSN <= 0 || baseLCR <= 0 || subbaseLCR <= 0) {
            throw new Error('Tutti i parametri devono essere maggiori di 0');
        }

        // Applica i moltiplicatori di stabilizzazione secondo Excel
        // Questi valori rappresentano l'aumento di efficacia strutturale
        const stabilisedSN = baseSN * baseLCR * subbaseLCR;

        return stabilisedSN;
    }

    /**
     * Calcola LogW18 usando la formula AASHTO Excel esatta.
     *
     * Formula Excel (celle M29/N29 di CALCULATION.txt):
     * LogW18 = f1 + (9.36*LOG10(SN+1)-0.2) + (f2/(0.4+(1094/(SN+1)^5.19))) + f3
     *
     * Questa formula calcola la capacità di traffico logaritmica della pavimentazione
     * basata sul suo Structural Number. SN diversi producono capacità diverse.
     *
     * Esempi da Excel:
     * - SN=6.78 → LogW18=8.620 → Capacità=417M ESAL
     * - SN=9.96 → LogW18=9.997 → Capacità=9.9B ESAL
     *
     * @param structuralNumber SN della pavimentazione
     * @param aashtoFactors Fattori f1, f2, f3 calcolati
     * @returns LogW18 (logaritmo base 10 della capacità di traffico)
     */
    public static calculateLogW18FromSN(structuralNumber: number, aashtoFactors: { f1: number; f2: number; f3: number }): number {
        if (structuralNumber <= 0) {
            throw new Error('Structural Number deve essere maggiore di 0');
        }

        const { f1, f2, f3 } = aashtoFactors;

        // Formula AASHTO Excel esatta (celle M29/N29)
        const term1 = f1;
        const term2 = 9.36 * Math.log10(structuralNumber + 1) - 0.2;
        const term3 = f2 / (0.4 + 1094 / Math.pow(structuralNumber + 1, 5.19));
        const term4 = f3;

        const logW18 = term1 + term2 + term3 + term4;

        // Test di validazione con valori Excel noti

        // Validazione automatica per i valori Excel noti
        if (Math.abs(structuralNumber - 6.78) < 0.01) {
            const expectedLogW18 = 8.62;
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const error = Math.abs(logW18 - expectedLogW18);
        }

        if (Math.abs(structuralNumber - 9.96) < 0.01) {
            const expectedLogW18 = 9.88;
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const error = Math.abs(logW18 - expectedLogW18);
        }

        return logW18;
    }

    /**
     * Calcola la capacità di traffico ESAL dalla LogW18.
     *
     * Formula: Capacità = 10^LogW18
     *
     * Esempi da Excel:
     * - LogW18=8.620 → Capacità=417,033,181 ESAL
     * - LogW18=9.997 → Capacità=9,934,845,103 ESAL
     *
     * @param logW18 Logaritmo base 10 della capacità di traffico
     * @returns Capacità di traffico in ESAL
     */
    public static calculateTrafficCapacityFromLogW18(logW18: number): number {
        return Math.pow(10, logW18);
    }
    /**
     * Calcola i fattori AASHTO f1, f2, f3 usando i parametri Excel specifici.
     *
     * Questi fattori sono calcolati dinamicamente usando:
     * - Reliability = 95% (da ProjectTypeService defaultValue)
     * - StandardError = 0.5 (da ProjectTypeService defaultValue)
     * - Mr = valore calcolato con formula Excel (non 1500×CBR)
     *
     * @param reliability Livello di affidabilità (%) - default 95
     * @param standardError Errore standard combinato So - default 0.5
     * @param mrPsi Modulo resiliente in psi (calcolato con formula Excel)
     * @param deltaPSI Differenza PSI (Po - Pt) - default 2.0
     * @returns Oggetto con i fattori f1, f2, f3 calcolati
     */
    public static calculateAASHTOFactorsExcel(params: { reliability?: number; standardError?: number; mrPsi: number; deltaPSI?: number }): { f1: number; f2: number; f3: number } {
        const {
            reliability = 95, // Default da ProjectTypeService
            standardError = 0.5, // Default da ProjectTypeService
            mrPsi,
            deltaPSI = 2.0, // Po=4.5, Pt=2.5 → 4.5-2.5=2.0
        } = params;

        // f1: Standard normal deviate factor
        // Formula Excel: ABS(NORMSINV(R/100)) × So
        const zR = this.calculateStandardNormalDeviate(reliability);
        const f1 = Math.abs(zR) * standardError;

        // f2: Serviceability factor
        // Formula Excel: LOG10(ΔPSI/(4.2-1.5))
        const f2 = Math.log10(deltaPSI / (4.2 - 1.5));

        // f3: Subgrade strength factor
        // Formula Excel: (2.32 × LOG10(Mr)) - 8.07
        const f3 = 2.32 * Math.log10(mrPsi) - 8.07;

        return { f1, f2, f3 };
    }

    /**
     * Calcola lo Standard Normal Deviate (ZR) per un dato livello di affidabilità.
     *
     * Formula Excel: NORMSINV(reliability/100) × -1
     *
     * @param reliabilityPercent Livello di affidabilità in percentuale
     * @returns Valore ZR (negativo per Excel compatibility)
     */
    private static calculateStandardNormalDeviate(reliabilityPercent: number): number {
        if (reliabilityPercent <= 0 || reliabilityPercent >= 100) {
            throw new Error('Reliability deve essere tra 0 e 100 (esclusivi)');
        }

        // Approssimazione della funzione inversa normale standard
        // Per reliability comune (90%, 95%, 99%)
        const reliabilityDecimal = reliabilityPercent / 100;

        // Approssimazione Beasley-Springer-Moro per NORMSINV
        const zR = this.approximateNormSInv(reliabilityDecimal);

        // Excel usa il valore negativo: NORMSINV(R/100) × -1
        return -zR;
    }

    /**
     * Approssimazione della funzione NORMSINV di Excel.
     * Implementazione semplificata per i valori common di reliability.
     */
    private static approximateNormSInv(p: number): number {
        // Per i valori comuni di reliability:
        if (p === 0.9) return 1.282;
        if (p === 0.95) return 1.645; // Caso più comune nel nostro sistema
        if (p === 0.99) return 2.326;

        // Approssimazione generale (Beasley-Springer-Moro algorithm simplified)
        const a = [0, -3.969683028665376e1, 2.209460984245205e2, -2.759285104469687e2, 1.38357751867269e2, -3.066479806614716e1, 2.506628277459239];
        const b = [0, -5.447609879822406e1, 1.615858368580409e2, -1.556989798598866e2, 6.680131188771972e1, -1.328068155288572e1];
        const c = [0, -7.784894002430293e-3, -3.223964580411365e-1, -2.400758277161838, -2.549732539343734, 4.374664141464968, 2.938163982698783];
        const d = [0, 7.784695709041462e-3, 3.224671290700398e-1, 2.445134137142996, 3.754408661907416];

        const x = p - 0.5;
        if (Math.abs(x) < 0.42) {
            const r = x * x;
            return (x * (((((a[6] * r + a[5]) * r + a[4]) * r + a[3]) * r + a[2]) * r + a[1])) / (((((b[5] * r + b[4]) * r + b[3]) * r + b[2]) * r + b[1]) * r + 1);
        }

        const r = p < 0.5 ? p : 1 - p;
        const t = Math.sqrt(-Math.log(r));
        const result = ((((c[4] * t + c[3]) * t + c[2]) * t + c[1]) * t + c[0]) / ((((d[4] * t + d[3]) * t + d[2]) * t + d[1]) * t + 1);

        return p < 0.5 ? -result : result;
    }

    /**
     * Metodo di convenienza per ottenere tutti i parametri Excel-specifici
     * da utilizzare nel Goal Seek, basati sui valori di default del form.
     *
     * @param userInputs Input dell'utente dal form
     * @returns Parametri completi per il calcolo Excel-compatibile
     */
    public static prepareExcelParameters(userInputs: {
        cbrSubgrade: number;
        nominalESAL: number;
        reliabilityLevel?: number;
        combinedStandardError?: number;
        initialServiceIndex?: number;
        terminalSurfaceIndex?: number;
    }) {
        const {
            cbrSubgrade,
            nominalESAL,
            reliabilityLevel = 95, // Default da ProjectTypeService
            combinedStandardError = 0.5, // Default da ProjectTypeService
            initialServiceIndex = 4.5, // Default da ProjectTypeService
            terminalSurfaceIndex = 2.5, // Default da ProjectTypeService
        } = userInputs;

        // Calcola Mr usando la formula Excel specifica
        const mrPsi = this.calculateSubgradeModulusExcel(cbrSubgrade);
        const mrKPa = this.calculateSubgradeModulusExcelKPa(cbrSubgrade);

        // Calcola il traffico design Excel
        const projectESAL = this.calculateProjectESALExcel(nominalESAL);
        const targetLogW18 = Math.log10(projectESAL);

        // Calcola ΔPSI
        const deltaPSI = initialServiceIndex - terminalSurfaceIndex;

        // Calcola i fattori AASHTO con parametri Excel
        const aashtoFactors = this.calculateAASHTOFactorsExcel({
            reliability: reliabilityLevel,
            standardError: combinedStandardError,
            mrPsi,
            deltaPSI,
        });

        return {
            // Parametri di input processati
            mrPsi,
            mrKPa,
            projectESAL,
            targetLogW18,
            deltaPSI,

            // Fattori AASHTO calcolati
            ...aashtoFactors,

            // Parametri originali per reference
            originalInputs: userInputs,
        };
    }
}
